import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChartLineUp, Calculator } from "@phosphor-icons/react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import SEO from "../components/SEO";

const fmtINR = (n) => {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const invested = payload.find((p) => p.dataKey === "invested")?.value || 0;
  const returns = payload.find((p) => p.dataKey === "returns")?.value || 0;
  const total = invested + returns;
  return (
    <div className="bg-black border border-white/15 px-3 py-2">
      <div className="overline mb-1">Year {label}</div>
      <div className="mono text-xs">invested <span className="text-white/70 ml-2">{fmtINR(invested)}</span></div>
      <div className="mono text-xs">returns <span className="text-bullish ml-2">{fmtINR(returns)}</span></div>
      <div className="mono text-xs">total <span className="text-white ml-2">{fmtINR(total)}</span></div>
    </div>
  );
}

export default function SIPCalculator() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("forward"); // "forward" | "goal"
  const [monthly, setMonthly] = useState(10000);
  const [target, setTarget] = useState(10000000); // ₹1 Cr default for goal mode
  const [years, setYears] = useState(15);
  const [rate, setRate] = useState(12);
  const [stepUp, setStepUp] = useState(0);

  // In Goal mode: solve for the monthly SIP required to hit the target.
  // FV = P * [((1+r)^n - 1) / r] * (1+r)  →  P = FV / { ... }
  const requiredMonthly = useMemo(() => {
    if (mode !== "goal") return null;
    const r = rate / 100 / 12;
    const n = years * 12;
    if (r === 0) return target / n;
    const factor = ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
    return factor > 0 ? target / factor : 0;
  }, [mode, target, years, rate]);

  const effectiveMonthly = mode === "goal" ? Math.max(0, requiredMonthly || 0) : monthly;

  const { invested, futureValue, returns, series } = useMemo(() => {
    const r = rate / 100 / 12;
    let totalInv = 0;
    let totalFv = 0;
    const sr = [];
    let monthlyAmt = effectiveMonthly;
    // Goal mode is single-shot (no step-up); forward mode uses stepUp.
    const yearlyStepUp = mode === "goal" ? 0 : stepUp;
    for (let y = 1; y <= years; y++) {
      const growth = Math.pow(1 + r, 12);
      const sipYearFv = monthlyAmt * ((growth - 1) / r) * (1 + r);
      totalFv = totalFv * growth + sipYearFv;
      totalInv += monthlyAmt * 12;
      sr.push({
        year: y,
        invested: Math.round(totalInv),
        returns: Math.max(0, Math.round(totalFv - totalInv)),
        total: Math.round(totalFv),
      });
      monthlyAmt = monthlyAmt * (1 + yearlyStepUp / 100);
    }
    return {
      invested: totalInv,
      futureValue: totalFv,
      returns: totalFv - totalInv,
      series: sr,
    };
  }, [effectiveMonthly, years, rate, stepUp, mode]);

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="sip-page">
      <SEO
        title="SIP Calculator & Goal Planner"
        path="/sip"
        description="Free SIP calculator for Indian investors with step-up SIP and reverse 'Goal Planner' mode — find out how much to invest monthly to reach ₹1 Cr, ₹5 Cr or any target corpus. Annuity-due formula, projection chart up to 40 years."
        keywords={["SIP calculator", "goal planner", "mutual fund SIP", "step up SIP calculator", "₹1 crore SIP", "FIRE calculator India", "retirement SIP calculator"]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "SIP Calculator & Goal Planner",
          url: "https://sent-i-meter.com/sip",
          applicationCategory: "FinanceApplication",
          offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
        }}
      />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10 px-4 md:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/70 hover:text-white" data-testid="sip-back">
            <ArrowLeft size={16} weight="bold" />
            <span className="overline">back</span>
          </button>
          <div className="w-px h-6 bg-white/10 mx-2" />
          <button onClick={() => navigate("/")} className="w-8 h-8 bg-white text-black flex items-center justify-center">
            <ChartLineUp size={16} weight="bold" />
          </button>
          <div className="hidden sm:block">
            <div className="heading text-sm font-bold tracking-tight leading-none">{mode === "goal" ? "GOAL PLANNER" : "SIP CALCULATOR"}</div>
            <div className="overline mt-0.5 leading-none">{mode === "goal" ? "solve for monthly sip" : "systematic investment plan"}</div>
          </div>
        </div>
        <Calculator size={18} weight="duotone" color="#FFCC00" />
      </header>

      <main className="px-4 md:px-6 py-6 max-w-[1300px] mx-auto">
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
          {/* Inputs */}
          <div className="bg-surface border border-white/10 p-5 lg:col-span-1" data-testid="sip-inputs">
            <h2 className="heading text-xl font-bold mb-3">Inputs</h2>

            {/* Mode toggle */}
            <div className="flex gap-1 mb-4 border border-white/10" data-testid="sip-mode-toggle">
              <button
                onClick={() => setMode("forward")}
                className={`flex-1 py-2 mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-150 ${
                  mode === "forward" ? "bg-white text-black" : "bg-transparent text-white/60 hover:text-white"
                }`}
                data-testid="sip-mode-forward"
              >
                Future Value
              </button>
              <button
                onClick={() => setMode("goal")}
                className={`flex-1 py-2 mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-150 ${
                  mode === "goal" ? "bg-white text-black" : "bg-transparent text-white/60 hover:text-white"
                }`}
                data-testid="sip-mode-goal"
              >
                Goal Planner
              </button>
            </div>

            {mode === "forward" && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="overline">monthly investment</span>
                  <span className="mono text-sm text-white">{fmtINR(monthly)}</span>
                </div>
                <input
                  type="number"
                  min="500"
                  max="1000000"
                  step="500"
                  value={monthly}
                  onChange={(e) => setMonthly(Math.max(500, +e.target.value || 0))}
                  className="w-full bg-black border border-white/10 px-3 py-2 mono text-sm focus:outline-none focus:border-white/30 mb-2"
                  data-testid="sip-monthly"
                />
                <input
                  type="range" min="500" max="200000" step="500" value={monthly}
                  onChange={(e) => setMonthly(+e.target.value)}
                  className="w-full accent-bullish"
                />
              </div>
            )}

            {mode === "goal" && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="overline">goal · target corpus</span>
                  <span className="mono text-sm text-neutral_zone">{fmtINR(target)}</span>
                </div>
                <input
                  type="number"
                  min="100000"
                  max="1000000000"
                  step="100000"
                  value={target}
                  onChange={(e) => setTarget(Math.max(100000, +e.target.value || 0))}
                  className="w-full bg-black border border-white/10 px-3 py-2 mono text-sm focus:outline-none focus:border-white/30 mb-2"
                  data-testid="sip-target"
                />
                <input
                  type="range" min="500000" max="100000000" step="500000" value={target}
                  onChange={(e) => setTarget(+e.target.value)}
                  className="w-full accent-bullish"
                />
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {[2500000, 5000000, 10000000, 20000000, 50000000].map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setTarget(preset)}
                      className={`px-2 py-0.5 mono text-[10px] uppercase tracking-[0.14em] border transition-colors duration-150 ${
                        target === preset
                          ? "bg-white text-black border-white"
                          : "bg-transparent text-white/50 border-white/10 hover:border-white/40 hover:text-white"
                      }`}
                      data-testid={`sip-target-preset-${preset}`}
                    >
                      {fmtINR(preset)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="overline">duration · years</span>
                <span className="mono text-sm text-white">{years}</span>
              </div>
              <input
                type="range" min="1" max="40" step="1" value={years}
                onChange={(e) => setYears(+e.target.value)}
                className="w-full accent-bullish"
                data-testid="sip-years"
              />
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="overline">expected return · % p.a.</span>
                <span className="mono text-sm text-white">{rate}%</span>
              </div>
              <input
                type="range" min="1" max="30" step="0.5" value={rate}
                onChange={(e) => setRate(+e.target.value)}
                className="w-full accent-bullish"
                data-testid="sip-rate"
              />
            </div>

            {mode === "forward" && (
              <div className="mb-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="overline">annual step-up · %</span>
                  <span className="mono text-sm text-white">{stepUp}%</span>
                </div>
                <input
                  type="range" min="0" max="25" step="1" value={stepUp}
                  onChange={(e) => setStepUp(+e.target.value)}
                  className="w-full accent-bullish"
                  data-testid="sip-stepup"
                />
                <div className="overline mt-1">increase your SIP every year (optional)</div>
              </div>
            )}

            {mode === "goal" && requiredMonthly != null && (
              <div className="mt-3 p-3 border border-bullish/40 bg-bullish/[0.04]" data-testid="sip-required">
                <div className="overline">required monthly sip</div>
                <div className="mono text-2xl font-bold text-bullish mt-0.5" data-testid="sip-required-amount">
                  {fmtINR(requiredMonthly)}
                </div>
                <div className="overline mt-1 text-white/50">to reach {fmtINR(target)} in {years}y @ {rate}%</div>
              </div>
            )}
          </div>

          {/* Results */}
          <div className="bg-surface border border-white/10 p-5 lg:col-span-2 grid-bg" data-testid="sip-results">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="heading text-xl font-bold">{mode === "goal" ? "Projection With Required SIP" : "Projected Outcome"} · {years}y</h2>
              {mode === "goal" && (
                <span className="overline" data-testid="sip-effective-monthly">monthly · {fmtINR(effectiveMonthly)}</span>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="border border-white/5 px-3 py-3">
                <div className="overline">invested</div>
                <div className="mono text-2xl font-bold text-white" data-testid="sip-invested">{fmtINR(invested)}</div>
              </div>
              <div className="border border-white/5 px-3 py-3">
                <div className="overline">est. returns</div>
                <div className="mono text-2xl font-bold text-bullish" data-testid="sip-returns">{fmtINR(returns)}</div>
              </div>
              <div className="border border-white/5 px-3 py-3">
                <div className="overline">total value</div>
                <div className="mono text-2xl font-bold text-neutral_zone" data-testid="sip-total">{fmtINR(futureValue)}</div>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="year" tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} />
                <YAxis tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} width={70} tickFormatter={(v) => fmtINR(v)} />
                <Tooltip content={<CustomTooltip />} cursor={{ stroke: "rgba(255,255,255,0.15)" }} />
                <Legend iconType="plainline" wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "#A1A1AA" }} />
                <Line type="monotone" dataKey="invested" stroke="#A1A1AA" strokeWidth={1.6} dot={false} name="invested" isAnimationActive={false} />
                <Line type="monotone" dataKey="total" stroke="#34C759" strokeWidth={2.2} dot={false} name="total value" isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        <footer className="border-t border-white/10 pt-4 mt-8 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="overline hover:text-white" data-testid="sip-footer-back">← back to dashboard</button>
          <span className="overline">illustrative only · not investment advice</span>
        </footer>
      </main>
    </div>
  );
}
